import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Brain, Sparkles, Target, CheckCircle, ArrowRight, User, LogOut, MoreVertical, LayoutDashboard, UserCircle, Settings, Send, HeartHandshake, MapPin, MessageCircle, Bell, Home, Sunrise, Crown } from 'lucide-react';
import VoiceInputButton from './VoiceInputButton';
import { getUserRoadmaps } from '../services/supabaseService';
import { useAuth } from '../context/AuthContext';
import faviconDark from '../assets/favicon-dark.png';
import { converseWithLunaStreaming, isRoadmapComplete, getRoadmapData } from '../services/lunaService';
import { callClaudeStreaming } from '../services/claudeAPI';
import Auth from './Auth';
import GoalSelectionHub from './GoalSelectionHub';
import MarkdownMessage from './MarkdownMessage';
import DreamCreationOverlay from './DreamCreationOverlay';
import DreamCreationLive from './DreamCreationLive';
import { useCreationProgress, CreationEvent, setGlobalProgressUpdater } from '../context/CreationProgressContext';

const LandingPageNew = ({ onComplete, onBack = null, onGoToDashboard = null, onGoToProfile = null, onGoToSettings = null, onGoToPricing = null, onOpenHomeHub = null, onOpenAssessment = null, onOpenPortfolioOverview = null, hasMultipleDreams = false, notificationCount = 0, isReturningUser = false }) => {
  const { user, loading: authLoading, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [hasExistingRoadmaps, setHasExistingRoadmaps] = useState(false);

  const initialStage = isReturningUser ? 'goalSelection' : 'hero';
  const [stage, setStage] = useState(initialStage);

  // Luna conversation state
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLunaTyping, setIsLunaTyping] = useState(false);
  const [streamingContent, setStreamingContent] = useState(''); // For streaming text
  const [partner1Name, setPartner1Name] = useState('');
  const [partner2Name, setPartner2Name] = useState('');
  const [lunaContext, setLunaContext] = useState({});
  const chatContainerRef = useRef(null);

  // Voice input state
  const [isVoiceListening, setIsVoiceListening] = useState(false);

  // Dream creation overlay state
  const [showCreationOverlay, setShowCreationOverlay] = useState(false);
  const [pendingRoadmapData, setPendingRoadmapData] = useState(null);
  const [pendingConversation, setPendingConversation] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // LIVE CREATION PROGRESS - Staff Engineer Pattern
  // Subscribe to progress events for immediate navigation to live preview
  // ═══════════════════════════════════════════════════════════════════════════
  const creationProgress = useCreationProgress();
  const hasNavigatedToLiveRef = useRef(false);

  // Wire up global progress updater so lunaService can emit events
  useEffect(() => {
    setGlobalProgressUpdater(creationProgress.updateProgress);
    return () => setGlobalProgressUpdater(null);
  }, [creationProgress.updateProgress]);

  // Subscribe to creation events - show transition before navigating
  useEffect(() => {
    const unsubscribe = creationProgress.subscribe((event) => {
      // When milestone generation starts, show transition message first
      if (event.type === CreationEvent.MILESTONE_GENERATING && !hasNavigatedToLiveRef.current) {
        console.log('🌟 MILESTONE_GENERATING detected - showing transition...');
        hasNavigatedToLiveRef.current = true;

        // Add Luna's farewell message to the conversation
        const dreamTitle = event.data?.title || 'your dream';
        const farewellMessage = {
          role: 'assistant',
          content: `**Perfect.** I have everything I need.\n\nI'm now creating your personalized roadmap for **"${dreamTitle}"**. This will just take a moment...\n\n*Handing you over to the creation studio...*`,
          isTransition: true
        };

        setConversation(prev => [...prev, farewellMessage]);
        setStreamingContent('');
        setIsLunaTyping(false);

        // Show transition stage briefly, then navigate to live preview
        setStage('transitioning');

        // After 2 seconds, switch to creation screen
        setTimeout(() => {
          console.log('🚀 Transition complete - navigating to live preview!');
          setStage('creatingLive');
        }, 2000);
      }
    });

    return unsubscribe;
  }, [creationProgress.subscribe]);

  // Reset navigation ref when stage changes away from creation flow
  useEffect(() => {
    if (stage !== 'creatingLive' && stage !== 'transitioning') {
      hasNavigatedToLiveRef.current = false;
    }
  }, [stage]);

  // Handle live creation completion
  const handleLiveCreationComplete = useCallback((roadmapData) => {
    console.log('✅ Live creation complete, transitioning to MilestoneDetailPage');
    creationProgress.reset();

    // ═══════════════════════════════════════════════════════════════════════
    // UI SAFEGUARD: Verify milestones exist before transitioning
    // A dream without milestones should never reach the user
    // ═══════════════════════════════════════════════════════════════════════
    const dataToUse = roadmapData || pendingRoadmapData;
    const milestones = dataToUse?.milestones || [];

    if (milestones.length === 0) {
      console.error('❌ UI SAFEGUARD: Cannot complete - no milestones in roadmap data');
      console.error('   This should not happen. Staying on creation page for retry.');
      // Don't navigate - let user retry
      return;
    }

    console.log(`✅ UI SAFEGUARD PASSED: ${milestones.length} milestone(s) found`);
    console.log('📦 Milestone data:', milestones[0]);

    // Pass to App.js handleLandingComplete - include savedRoadmapId to prevent duplicates
    const completeData = {
      chosenPath: 'luna',
      milestones: milestones,
      partner1: dataToUse?.partner1 || partner1Name || '',
      partner2: dataToUse?.partner2 || partner2Name || '',
      location: dataToUse?.location || '',
      conversationHistory: conversation,
      // CRITICAL: Pass saved roadmap ID - Luna already saved to DB
      savedRoadmapId: dataToUse?.savedRoadmapId || null
    };

    console.log('🚀 Calling onComplete with:', completeData);
    onComplete(completeData);
  }, [creationProgress, onComplete, conversation, pendingRoadmapData, partner1Name, partner2Name]);

  // Handle live creation error
  const handleLiveCreationError = useCallback((error) => {
    console.error('❌ Live creation error:', error);
    // Stay on live page - it will show retry option
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation, streamingContent]);

  // Check if user has existing roadmaps
  useEffect(() => {
    const checkRoadmaps = async () => {
      if (user && !authLoading) {
        try {
          const { data: roadmaps } = await getUserRoadmaps();
          setHasExistingRoadmaps(roadmaps && roadmaps.length > 0);
        } catch (error) {
          console.error('Error checking roadmaps:', error);
        }
      } else {
        setHasExistingRoadmaps(false);
      }
    };
    checkRoadmaps();
  }, [user, authLoading]);

  const handleGetStarted = () => {
    setStage('pathChoice');
  };

  const handlePathChoice = async (chosenPath) => {
    if (chosenPath === 'luna') {
      setStage('lunaConversation');
      setIsLunaTyping(true);
      setStreamingContent('');

      // Luna's greeting system prompt (simplified for initial greeting)
      const greetingPrompt = `You are Luna, a warm and intelligent AI planning assistant for couples.

This is the user's FIRST message - they just said "Hi" to start the conversation.

Your response should be:
1. A friendly, warm greeting (use an emoji or two)
2. Brief introduction of yourself
3. An open-ended question about what they're hoping to achieve together

Keep it conversational and under 100 words. Use **bold** for emphasis on key phrases.`;

      try {
        // Use a ref-like approach to capture the accumulated content
        let accumulatedContent = '';

        // Use streaming for the initial greeting
        await callClaudeStreaming(
          [{ role: 'user', content: 'Hi' }],
          { systemPrompt: greetingPrompt, maxTokens: 512, temperature: 0.9 },
          {
            onChunk: (text) => {
              accumulatedContent += text;
              setStreamingContent(accumulatedContent);
            },
            onDone: () => {
              // Move streaming content to conversation
              if (accumulatedContent) {
                setConversation([{ role: 'assistant', content: accumulatedContent }]);
              }
              setStreamingContent('');
              setIsLunaTyping(false);
            },
            onError: (error) => {
              console.error('Streaming error:', error);
              // Fallback to non-streaming
              setStreamingContent('');
              setConversation([{
                role: 'assistant',
                content: "Hey there! 👋 Welcome! I'm Luna, your planning assistant for couples. I'm here to help you and your partner create a realistic roadmap for whatever you're dreaming about together – whether it's buying a home, planning a wedding, starting a business, or **literally anything else** you want to achieve as a team.\n\n**So, what brings you here today?** What's something you and your partner are thinking about doing together? 💭"
              }]);
              setIsLunaTyping(false);
            }
          }
        );
      } catch (error) {
        console.error('Error getting Luna response:', error);
        setStreamingContent('');
        setConversation([{
          role: 'assistant',
          content: "Hey there! 👋 Welcome! I'm Luna, your planning assistant for couples. I'm here to help you and your partner create a realistic roadmap for whatever you're dreaming about together.\n\n**So, what brings you here today?** What's something you and your partner are thinking about doing together? 💭"
        }]);
        setIsLunaTyping(false);
      }
    } else if (chosenPath === 'compatibility') {
      onComplete({ chosenPath: 'compatibility' });
    } else if (chosenPath === 'ready') {
      setStage('goalSelection');
    }
  };

  const handleGoalSelectionPath = (pathId) => {
    if (pathId === 'luna') {
      handlePathChoice('luna');
    } else if (pathId === 'templates') {
      onComplete({
        chosenPath: 'ready',
        showTemplates: true
      });
    } else if (pathId === 'custom') {
      onComplete({
        chosenPath: 'ready',
        showCustomCreator: true
      });
    }
  };

  const handleLunaSendMessage = async () => {
    if (!userInput.trim() || isLunaTyping) return;

    const userMessage = { role: 'user', content: userInput };
    const newConversation = [...conversation, userMessage];
    setConversation(newConversation);
    setUserInput('');
    setIsLunaTyping(true);
    setStreamingContent('');

    try {
      // Use streaming Luna service - text appears as it's generated
      const claudeMessages = newConversation.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const result = await converseWithLunaStreaming(claudeMessages, lunaContext, {
        // Called for each text chunk - update UI in real-time
        onChunk: (chunk, fullText) => {
          setStreamingContent(fullText);
        },
        // Called when streaming completes
        onComplete: (completeResult) => {
          console.log('✅ Luna streaming complete');
        },
        // Called on error
        onError: (error) => {
          console.error('❌ Luna streaming error:', error);
        }
      });

      // Streaming complete - finalize the message
      setStreamingContent('');
      setConversation([...newConversation, { role: 'assistant', content: result.message }]);
      setLunaContext(result.context);

      if (isRoadmapComplete(result.context)) {
        const roadmapData = getRoadmapData(result.context);
        // Store data for DreamCreationLive's completion handler
        setPendingRoadmapData(roadmapData);
        setPendingConversation([...newConversation, { role: 'assistant', content: result.message }]);
        // DreamCreationLive handles progress UI - don't show redundant overlay
      }
    } catch (error) {
      console.error('❌ Error in Luna conversation:', error);
      setStreamingContent('');
      setConversation([...newConversation, {
        role: 'assistant',
        content: "I'm having a moment of trouble connecting. Could you tell me again what you're hoping to accomplish?"
      }]);
    }
    setIsLunaTyping(false);
  };

  // Handle overlay completion - navigate to MilestoneDetailPage
  const handleCreationComplete = () => {
    if (pendingRoadmapData) {
      const milestones = pendingRoadmapData.milestones || [];

      if (milestones.length === 0) {
        console.error('❌ UI SAFEGUARD: Cannot complete - no milestones');
        return;
      }

      // Pass to App.js handleLandingComplete - include savedRoadmapId to prevent duplicates
      onComplete({
        chosenPath: 'luna',
        milestones: milestones,
        partner1: pendingRoadmapData.partner1 || partner1Name || '',
        partner2: pendingRoadmapData.partner2 || partner2Name || '',
        location: pendingRoadmapData.location || '',
        conversationHistory: pendingConversation,
        savedRoadmapId: pendingRoadmapData.savedRoadmapId || null
      });
    }
  };

  // Handle voice transcript - populate input field with spoken text
  const handleVoiceTranscript = useCallback((text) => {
    setUserInput(text);
  }, []);

  // Handle voice listening state change
  const handleVoiceListeningChange = useCallback((listening) => {
    setIsVoiceListening(listening);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-stone-900 font-sans selection:bg-orange-100 selection:text-orange-900">

      {/* NAVIGATION */}
      <nav className="w-full px-8 py-6 flex justify-between items-center max-w-7xl mx-auto sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <img src={faviconDark} alt="TwogetherForward" className="w-8 h-8" />
          <span className="font-serif text-xl font-bold tracking-tight">TwogetherForward</span>
        </div>

        <div className="flex items-center gap-4">
          {!authLoading && (
            <>
              {/* Pricing link for everyone */}
              {onGoToPricing && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onGoToPricing}
                  className="text-sm font-medium text-stone-700 hover:text-stone-900 transition"
                >
                  Pricing
                </motion.button>
              )}

              {user ? (
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="bg-white border border-stone-200 rounded-full px-4 py-2 flex items-center gap-2 text-sm font-medium hover:bg-stone-50 transition"
                  >
                    <User className="w-4 h-4 text-stone-700" />
                    <span className="text-stone-900">{user.email?.split('@')[0]}</span>
                    <MoreVertical className="w-4 h-4 text-stone-500" />
                  </motion.button>

                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute right-0 mt-2 bg-white border border-stone-200 rounded-2xl p-2 min-w-[240px] shadow-xl"
                    >
                      {/* User Info Header */}
                      <div className="px-3 py-2 border-b border-stone-100 mb-2">
                        <p className="text-xs text-stone-500">Signed in as</p>
                        <p className="text-sm font-medium text-stone-900">{user.email}</p>
                      </div>

                      {/* Planning Tools Section */}
                      <div className="mb-2">
                        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-stone-400">Planning Tools</p>
                        {onGoToDashboard && (
                          <button onClick={() => { onGoToDashboard(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <LayoutDashboard className="w-4 h-4 text-stone-700" />
                            Dashboard
                          </button>
                        )}
                        {onOpenHomeHub && (
                          <button onClick={() => { onOpenHomeHub(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <Sunrise className="w-4 h-4 text-amber-600" />
                            Home Hub
                          </button>
                        )}
                        {onOpenAssessment && (
                          <button onClick={() => { onOpenAssessment(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <HeartHandshake className="w-4 h-4 text-rose-600" />
                            Alignment Test
                          </button>
                        )}
                        {onOpenPortfolioOverview && hasMultipleDreams && (
                          <button onClick={() => { onOpenPortfolioOverview(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <Target className="w-4 h-4 text-stone-700" />
                            Portfolio Overview
                          </button>
                        )}
                      </div>

                      <div className="border-t border-stone-100 my-2"></div>

                      {/* Account Section */}
                      <div className="mb-2">
                        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-stone-400">Your Account</p>
                        {onGoToPricing && (
                          <button onClick={() => { onGoToPricing(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm font-semibold mb-2"
                            style={{
                              background: 'linear-gradient(135deg, #C4785A 0%, #d4916f 100%)',
                              color: 'white',
                              boxShadow: '0 2px 8px rgba(196, 120, 90, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <Crown className="w-4 h-4" />
                            Upgrade to Twogether Pro
                          </button>
                        )}
                        <button onClick={() => { /* TODO: Open notifications panel */ setShowUserMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                          <Bell className="w-4 h-4 text-stone-700" />
                          <span className="flex-1 text-left">Notifications</span>
                          {notificationCount > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              {notificationCount > 9 ? '9+' : notificationCount}
                            </span>
                          )}
                        </button>
                        {onGoToProfile && (
                          <button onClick={() => { onGoToProfile(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <UserCircle className="w-4 h-4 text-stone-700" />
                            Profile
                          </button>
                        )}
                        {onGoToSettings && (
                          <button onClick={() => { onGoToSettings(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition-colors text-sm text-stone-900">
                            <Settings className="w-4 h-4 text-stone-700" />
                            Settings
                          </button>
                        )}
                      </div>

                      <div className="border-t border-stone-100 my-2"></div>

                      {/* Sign Out */}
                      <button onClick={async () => { await signOut(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm text-red-600">
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </div>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAuthModal(true)}
                  className="bg-stone-900 text-[#FDFCF8] px-5 py-2 rounded-full text-sm font-medium hover:bg-stone-800 transition shadow-lg shadow-stone-900/10">
                  <User className="w-4 h-4 inline mr-2" />
                  Sign In
                </motion.button>
              )}
            </>
          )}
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuthModal && !user && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative max-w-md w-full">
            <button onClick={() => setShowAuthModal(false)}
              className="absolute -top-4 -right-4 bg-white border border-stone-200 w-10 h-10 rounded-full flex items-center justify-center text-2xl z-10 text-stone-900 hover:bg-stone-50 transition">×</button>
            <Auth onSuccess={() => setShowAuthModal(false)} />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {stage === 'hero' && (
          <motion.div
            key="hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* HERO SECTION */}
            <header className="max-w-7xl mx-auto px-6 pt-20 pb-32 md:pt-32 md:pb-40 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-stone-200 bg-white text-xs font-medium text-stone-500 uppercase tracking-wide">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Now available for Early Access
                </div>

                <h1 className="text-5xl md:text-7xl font-serif leading-[1.1] text-stone-900">
                  Love is easy.<br />
                  <span className="text-stone-400 italic">Logistics are hard.</span>
                </h1>

                <p className="text-xl text-stone-600 leading-relaxed max-w-lg">
                  The operating system for your shared life. Align on goals, plan big moves, and navigate finances with Luna—your proactive AI companion.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    onClick={handleGetStarted}
                    className="px-8 py-4 bg-stone-900 text-[#FDFCF8] rounded-full font-medium text-lg hover:bg-stone-800 transition flex items-center justify-center gap-2 shadow-xl shadow-stone-900/20">
                    Start Planning Together <ArrowRight size={18} />
                  </button>
                  {user && hasExistingRoadmaps && onGoToDashboard && (
                    <button
                      onClick={onGoToDashboard}
                      className="px-8 py-4 bg-white border border-stone-200 text-stone-900 rounded-full font-medium text-lg hover:bg-stone-50 transition flex items-center justify-center">
                      <LayoutDashboard className="w-5 h-5 mr-2" />
                      My Dashboard
                    </button>
                  )}
                </div>

                <p className="text-sm text-stone-400 pt-2">
                  2 dreams included • No credit card
                </p>
              </div>

              {/* HERO VISUAL */}
              <div className="relative h-[500px] w-full bg-stone-100 rounded-t-[200px] rounded-b-[40px] overflow-hidden border border-stone-200 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 to-stone-200 flex items-center justify-center">
                  <div className="text-center p-8">
                    <Sparkles className="w-16 h-16 text-stone-400 mx-auto mb-4" />
                    <p className="font-serif italic text-2xl text-stone-400">Your journey starts here</p>
                  </div>
                </div>

                {/* Floating UI Element overlay */}
                <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 w-3/4 bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-stone-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                      <Sparkles size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-stone-900 mb-1">Luna's Insight</p>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        "I found three neighborhoods that offer both the garden Sarah wants and Mike's short commute within your budget."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* PROBLEM SECTION */}
            <section className="py-24 bg-white border-t border-stone-100">
              <div className="max-w-4xl mx-auto px-6 text-center">
                <h2 className="text-3xl md:text-4xl font-serif mb-6 text-stone-900">
                  Why do we plan our careers with tools,<br/> but leave our relationships to chance?
                </h2>
                <p className="text-lg text-stone-500 leading-relaxed mb-16 max-w-2xl mx-auto">
                  Spreadsheets are cold. Therapy is expensive. Text chains get lost.
                  TwogetherForward bridges the gap between emotional connection and practical execution.
                </p>
              </div>

              <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { icon: <MessageCircle />, title: "The Friction", desc: "Endless debates about 'where do we start?'" },
                  { icon: <MapPin />, title: "The Overwhelm", desc: "Visas, weddings, mortgages—too many moving parts." },
                  { icon: <HeartHandshake />, title: "The Misalignment", desc: "Assuming you're on the same page, until you aren't." }
                ].map((item, i) => (
                  <div key={i} className="p-8 rounded-3xl bg-stone-50 border border-stone-100 hover:border-stone-300 transition duration-300">
                    <div className="w-12 h-12 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-stone-900 mb-6 shadow-sm">
                      {React.cloneElement(item.icon, { strokeWidth: 1.5 })}
                    </div>
                    <h3 className="text-xl font-bold text-stone-900 mb-3">{item.title}</h3>
                    <p className="text-stone-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FEATURES SECTION */}
            <section className="py-32 overflow-hidden">
              <div className="max-w-7xl mx-auto px-6">
                {/* Feature 1: Luna AI */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
                  <div>
                    <span className="text-orange-600 font-bold tracking-wider uppercase text-xs mb-2 block">Feature 01 — Intelligence</span>
                    <h2 className="text-4xl md:text-5xl font-serif mb-6 text-stone-900">Chat with Luna,<br/>your AI planner.</h2>
                    <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                      Luna analyzes your goals, location, and budget to give you instant, personalized advice. She remembers everything and helps you avoid costly mistakes.
                    </p>

                    <ul className="space-y-4">
                      {["Instant expert advice", "Location-specific costs", "Context-aware planning"].map((feat, i) => (
                        <li key={i} className="flex items-center gap-3 text-stone-800 font-medium">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs">
                            <CheckCircle size={14} strokeWidth={3} />
                          </div>
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative">
                    <div className="relative z-10 bg-white rounded-3xl shadow-2xl border border-stone-100 p-8 max-w-md mx-auto">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center">
                          <Brain className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-stone-900">Luna AI</p>
                          <p className="text-xs text-stone-400">Your Planning Assistant</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="bg-stone-50 border border-stone-100 rounded-lg p-3">
                          <p className="text-sm text-stone-900">
                            "For a Paris wedding with 80-100 guests, you're looking at €25-35K. Want me to break down where every euro goes?"
                          </p>
                        </div>
                        <div className="flex justify-end">
                          <div className="bg-stone-900 rounded-lg p-3 max-w-[70%]">
                            <p className="text-sm text-white">Yes! What about hidden costs?</p>
                          </div>
                        </div>
                        <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                          <div className="flex gap-2 items-center mb-2">
                             <Sparkles size={14} className="text-orange-600" />
                             <p className="text-xs text-orange-600 font-bold uppercase">Luna Suggests</p>
                          </div>
                          <p className="text-sm text-stone-800">
                            "Hidden costs like alterations (€300), vendor meals (€200), and overtime charges (€500) add up to ~€1,000..."
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Decorative circle behind */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-stone-100 rounded-full -z-10"></div>
                  </div>
                </div>

                {/* Feature 2: Budget Tracking */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div className="order-2 lg:order-1 relative">
                    <div className="relative z-10 bg-white rounded-3xl shadow-2xl border border-stone-100 p-8 max-w-md mx-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg text-stone-900">Wedding Budget</h3>
                        <Target className="w-6 h-6 text-stone-700" />
                      </div>

                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2 text-stone-900">
                          <span>€18,500 spent</span>
                          <span className="font-bold">74% of budget</span>
                        </div>
                        <div className="w-full h-4 bg-stone-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: '74%' }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.5 }}
                            className="h-full bg-emerald-500 rounded-full"
                          ></motion.div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-stone-50 border border-stone-100 rounded-lg p-3">
                          <div className="text-xs mb-1 text-stone-500">Remaining</div>
                          <div className="text-2xl font-bold text-emerald-600">€6,500</div>
                        </div>
                        <div className="bg-stone-50 border border-stone-100 rounded-lg p-3">
                          <div className="text-xs mb-1 text-stone-500">Status</div>
                          <div className="text-2xl font-bold text-emerald-600">On Track</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {[
                          ['Venue & Catering', '€13,750'],
                          ['Photography', '€3,000'],
                          ['Attire & Flowers', '€1,750']
                        ].map(([label, amount], i) => (
                          <div key={i} className="flex justify-between text-sm text-stone-900">
                            <span>{label}</span>
                            <span className="font-semibold">{amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-stone-100 rounded-full -z-10"></div>
                  </div>

                  <div className="order-1 lg:order-2">
                    <span className="text-emerald-600 font-bold tracking-wider uppercase text-xs mb-2 block">Feature 02 — Tracking</span>
                    <h2 className="text-4xl md:text-5xl font-serif mb-6 text-stone-900">Track every euro,<br/>avoid overspending.</h2>
                    <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                      Advanced budget tracker shows you exactly where your money goes. Set limits, track progress, and get smart alerts before you overspend.
                    </p>

                    <ul className="space-y-4">
                      {["Real-time updates", "Category breakdown", "Smart overspending alerts"].map((feat, i) => (
                        <li key={i} className="flex items-center gap-3 text-stone-800 font-medium">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs">
                            <CheckCircle size={14} strokeWidth={3} />
                          </div>
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA SECTION */}
            <section className="py-32 px-6 text-center bg-stone-50 border-t border-stone-100">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-5xl md:text-6xl font-serif text-stone-900 mb-8">
                  Start your next chapter.
                </h2>
                <p className="text-xl text-stone-600 mb-10">
                  Join couples who are turning their relationship dreams into achievable, step-by-step plans.
                </p>

                <button
                  onClick={handleGetStarted}
                  className="px-8 py-4 bg-stone-900 text-[#FDFCF8] rounded-full font-medium text-lg hover:bg-stone-800 transition shadow-xl shadow-stone-900/20 inline-flex items-center gap-2">
                  Start Planning Together <ArrowRight size={18} />
                </button>
                <p className="text-xs text-stone-400 mt-4">2 dreams included • Upgrade anytime</p>
              </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-white py-12 border-t border-stone-100">
              <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-2">
                  <img src={faviconDark} alt="TwogetherForward" className="w-6 h-6" />
                  <span className="font-serif font-bold text-stone-900">TwogetherForward</span>
                </div>

                <div className="flex gap-8 text-sm text-stone-500">
                  <a href="/privacy.html" className="hover:text-stone-900">Privacy Policy</a>
                  <a href="/terms.html" className="hover:text-stone-900">Terms of Service</a>
                  <a href="mailto:support@twogetherforward.com" className="hover:text-stone-900">Contact</a>
                </div>

                <p className="text-sm text-stone-400">© 2025 TwogetherForward</p>
              </div>
            </footer>
          </motion.div>
        )}

        {/* PATH CHOICE STAGE */}
        {stage === 'pathChoice' && (
          <motion.div
            key="pathChoice"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4 py-20"
          >
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-serif mb-4 text-stone-900">
                  Choose Your Starting Point
                </h2>
                <p className="text-xl text-stone-600">
                  Every couple's journey is unique. Pick the path that feels right for you.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Luna AI */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ y: -4 }}
                  className="bg-white border-2 border-stone-200 rounded-3xl p-8 relative cursor-pointer hover:border-stone-400 transition"
                  onClick={() => handlePathChoice('luna')}
                >
                  <div className="absolute -top-3 right-6 bg-stone-900 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </div>

                  <div className="w-16 h-16 rounded-2xl bg-stone-900 flex items-center justify-center mb-6">
                    <Brain className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold mb-3 font-serif text-stone-900">
                    Chat with Luna AI
                  </h3>
                  <p className="text-base mb-6 text-stone-600">
                    Our AI guide creates a personalized dream plan through conversation
                  </p>

                  <div className="space-y-2 mb-6">
                    {["Instant dream creation", "Tailored to your situation", "5-7 minute conversation"].map((feat, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                        <span className="text-sm text-stone-700">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-center gap-2 text-stone-900 font-semibold">
                    <span>Start Planning</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </motion.div>

                {/* Compatibility */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-stone-200 rounded-3xl p-8 cursor-pointer hover:border-stone-400 transition"
                  onClick={() => handlePathChoice('compatibility')}
                >
                  <div className="w-16 h-16 rounded-2xl bg-stone-100 border border-stone-200 flex items-center justify-center mb-6">
                    <Heart className="w-8 h-8 text-stone-900" />
                  </div>

                  <h3 className="text-2xl font-bold mb-3 font-serif text-stone-900">
                    Alignment Assessment
                  </h3>
                  <p className="text-base mb-6 text-stone-600">
                    Understand your compatibility before planning your future together
                  </p>

                  <div className="space-y-2 mb-6">
                    {["Identify areas of alignment", "Get discussion guide for gaps", "5-7 minute assessment"].map((feat, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                        <span className="text-sm text-stone-700">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-center gap-2 text-stone-900 font-semibold">
                    <span>Take Assessment</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </motion.div>

                {/* Quick Start */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-stone-200 rounded-3xl p-8 cursor-pointer hover:border-stone-400 transition"
                  onClick={() => handlePathChoice('ready')}
                >
                  <div className="w-16 h-16 rounded-2xl bg-stone-100 border border-stone-200 flex items-center justify-center mb-6">
                    <Target className="w-8 h-8 text-stone-900" />
                  </div>

                  <h3 className="text-2xl font-bold mb-3 font-serif text-stone-900">
                    Jump to Planning
                  </h3>
                  <p className="text-base mb-6 text-stone-600">
                    Already aligned? Skip straight to building your roadmap
                  </p>

                  <div className="space-y-2 mb-6">
                    {["Browse goal templates", "Build custom milestones", "Start planning immediately"].map((feat, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                        <span className="text-sm text-stone-700">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-center gap-2 text-stone-900 font-semibold">
                    <span>Start Now</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-center mt-12"
              >
                <button
                  onClick={() => setStage('hero')}
                  className="text-sm font-medium hover:underline text-stone-500"
                >
                  ← Back to home
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* LUNA CONVERSATION STAGE */}
        {stage === 'lunaConversation' && (
          <motion.div
            key="lunaConversation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4 py-20"
          >
            <div className="max-w-4xl mx-auto w-full">
              {/* Luna Profile */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white border border-stone-200 rounded-2xl p-4 mb-4 shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-stone-900 font-serif">
                      Luna - Your AI Planner
                    </h3>
                    <p className="text-sm text-stone-500">Online • Ready to help</p>
                  </div>
                  <button
                    onClick={() => setStage('pathChoice')}
                    className="text-sm text-stone-600 hover:text-stone-900 font-medium"
                  >
                    ← Back
                  </button>
                </div>
              </motion.div>

              {/* Chat Container */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-lg"
                style={{ height: '500px', display: 'flex', flexDirection: 'column' }}
              >
                {/* Chat Messages */}
                <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4">
                  {conversation.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl p-4 ${
                          message.role === 'user'
                            ? 'bg-stone-900 text-white'
                            : 'bg-stone-50 border border-stone-200 text-stone-900'
                        }`}
                      >
                        {message.role === 'user' ? (
                          message.content
                        ) : (
                          <MarkdownMessage content={message.content} />
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {/* Show streaming content while Luna is typing */}
                  {streamingContent && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="max-w-[80%] bg-stone-50 border border-stone-200 rounded-2xl p-4 text-stone-900">
                        <MarkdownMessage content={streamingContent} isStreaming={true} />
                      </div>
                    </motion.div>
                  )}

                  {/* Show typing indicator only when waiting for response (no streaming yet) */}
                  {isLunaTyping && !streamingContent && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4">
                        <div className="flex gap-1">
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.6, repeat: Infinity }}
                            className="w-2 h-2 bg-stone-400 rounded-full"
                          />
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                            className="w-2 h-2 bg-stone-400 rounded-full"
                          />
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                            className="w-2 h-2 bg-stone-400 rounded-full"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Input Area */}
                <div className="border-t border-stone-200 p-4 bg-stone-50">
                  <div className="flex gap-2 items-center">
                    {/* Text Input with Voice Indicator */}
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleLunaSendMessage()}
                        placeholder={isVoiceListening ? 'Listening...' : 'Type or speak your message...'}
                        className={`w-full bg-white border rounded-full px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-stone-900 text-stone-900 transition-all ${
                          isVoiceListening
                            ? 'border-red-300 ring-2 ring-red-100'
                            : 'border-stone-200'
                        }`}
                        disabled={isLunaTyping}
                      />
                      {/* Inline Voice Button */}
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <VoiceInputButton
                          onTranscript={handleVoiceTranscript}
                          onListeningChange={handleVoiceListeningChange}
                          disabled={isLunaTyping}
                          size="sm"
                          showTooltip={true}
                        />
                      </div>
                    </div>

                    {/* Send Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleLunaSendMessage}
                      disabled={isLunaTyping || !userInput.trim()}
                      className="bg-stone-900 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-800 transition"
                    >
                      <Send className="w-5 h-5" />
                    </motion.button>
                  </div>

                  {/* Voice Input Hint (shown on first use) */}
                  {isVoiceListening && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-stone-500 mt-2 text-center"
                    >
                      Speak naturally. Tap the microphone again when done.
                    </motion.p>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* TRANSITION STAGE - Luna hands off to creation */}
        {stage === 'transitioning' && (
          <motion.div
            key="transitioning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4 py-20 bg-gradient-to-br from-[#FDFCF8] via-stone-50 to-orange-50/30"
          >
            <div className="max-w-lg mx-auto text-center">
              {/* Luna Avatar with pulse animation */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative mb-8"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0.8, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-stone-300 to-stone-400"
                  style={{ filter: 'blur(20px)' }}
                />
                <div className="relative w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center shadow-xl">
                  <Brain className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              {/* Farewell message */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-4">
                  Creating Your Dream
                </h2>
                <p className="text-lg text-stone-600 mb-8">
                  Luna is preparing your personalized roadmap...
                </p>
              </motion.div>

              {/* Animated dots */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center gap-2"
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      y: [0, -8, 0],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: i * 0.15
                    }}
                    className="w-3 h-3 rounded-full bg-stone-400"
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* LIVE CREATION STAGE - Shows real-time progress (0-100%) */}
        {stage === 'creatingLive' && (
          <motion.div
            key="creatingLive"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
          >
            <DreamCreationLive
              onComplete={handleLiveCreationComplete}
              onError={handleLiveCreationError}
              onRetry={() => {
                // Navigate back to Luna chat so user can try again
                console.log('🔄 Retry requested - navigating back to Luna chat');
                creationProgress.reset();
                hasNavigatedToLiveRef.current = false;
                setStage('lunaConversation');
              }}
            />
          </motion.div>
        )}

        {/* GOAL SELECTION STAGE */}
        {stage === 'goalSelection' && (
          <motion.div
            key="goalSelection"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute top-4 left-4 z-50">
              <button
                onClick={() => setStage('pathChoice')}
                className="bg-white border border-stone-200 px-4 py-2 rounded-full font-medium flex items-center gap-2 text-stone-900 hover:bg-stone-50 transition"
              >
                ← Back to Path Selection
              </button>
            </div>
            <GoalSelectionHub
              partner1={partner1Name || 'Partner'}
              partner2={partner2Name || 'You'}
              onSelectPath={handleGoalSelectionPath}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dream Creation Overlay */}
      <DreamCreationOverlay
        isVisible={showCreationOverlay}
        dreamTitle={pendingRoadmapData?.milestones?.[0]?.title || pendingRoadmapData?.title || 'Your Dream'}
        dreamDetails={{
          budget: pendingRoadmapData?.milestones?.[0]?.budget || pendingRoadmapData?.estimatedCost,
          timeline: pendingRoadmapData?.milestones?.[0]?.timeline_months || pendingRoadmapData?.timelineMonths,
          location: pendingRoadmapData?.location
        }}
        onComplete={handleCreationComplete}
      />
    </div>
  );
};

export default LandingPageNew;
